import { FC, useMemo } from 'react'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { FloatingPane, TransactionRecatFloatingPane as TransactionRecatFloatingPaneType } from 'interfaces/floatingPanes'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { TransactionRecatForm } from 'components/features/pfm/TransactionRecatForm/TransactionRecatForm'
import { SimilarTransactionsForm } from 'components/features/pfm/SimilarTransactionsForm/SimilarTransactionsForm'
import { metaSelector } from 'selectors/globalSelectors'
import { getFormValues } from 'redux-form'
import { FORM_RECAT_TRANSACTION } from 'constants/forms'
import { TransactionRecatFormValues } from 'interfaces/form'
import { saturateCategory } from 'helpers/categoriesHelpers'
import { SimpleGuard, Guards } from 'interfaces/guards'
import { getItemFromCollection } from 'helpers/normalizeHelpers'
import { isFetchableLoaded, connectLoadable } from 'helpers/loadableHelpers'
import { getSimilarTransactionsActionCreator } from 'actions/moneyflowActions'
import { TransactionUid } from 'interfaces/moneyflow'

type SimilarTransactionsLoader = { transactionUid: TransactionUid }
const similarTransactionsGuard: SimpleGuard<SimilarTransactionsLoader> = {
  name: 'Similar Transactions Link',
  checker: (state) => {
    const pane = getItemFromCollection(state.app.floatingPanes.panes, 'transaction-recat') as TransactionRecatFloatingPaneType | null
    if (!pane) {
      return false
    }

    return isFetchableLoaded(pane.similarTransactions)
  },
  loader: (dispatch, ownProps) => dispatch(getSimilarTransactionsActionCreator.request({
    targetTransactionId: ownProps.transactionUid,
  })),
}
const guards: Guards = [
  similarTransactionsGuard,
]

const SimilarTransactionsLoader = connectLoadable<{}, {}, SimilarTransactionsLoader>(guards, () => null)(() => ({}), () => ({}))(() => null)


const TYPED_PANE: Pick<FloatingPane, 'type'> = {
  type: 'transaction-recat',
}

const recatFormValuesSelector = getFormValues<TransactionRecatFormValues>(FORM_RECAT_TRANSACTION)

export const TransactionRecatFloatingPane: FC<{ onClose: () => void }> = ({ onClose }) => {
  const paneSelector = useMemo(
    createFloatingPaneByTypeSelector,
    []
  )

  const pane = useStoreSelector((state) => paneSelector(state, TYPED_PANE)) as TransactionRecatFloatingPaneType | null
  const meta = useStoreSelector(metaSelector)

  const recatFormValues = useStoreSelector(recatFormValuesSelector)

  const subpaneChild = useMemo(() => {
    if (!pane || !meta) {
      return null
    }

    if (pane.mode === 'category') {
      return (
        <>
          { pane.transaction && <SimilarTransactionsLoader transactionUid={ pane.transaction.uid } /> }
          <TransactionRecatForm { ...pane } meta={ meta } onClose={ onClose } />
        </>
      )
    }

    if (pane.mode === 'similar-transactions') {
      if (!recatFormValues.categoryId) {
        return null
      }
      const category = saturateCategory(recatFormValues.categoryId, meta)

      return (
        <SimilarTransactionsForm
          { ...pane }
          recatApplyToAll={ recatFormValues.apply_to_all }
          category={ category }
        />
      )
    }

    return null
  }, [pane, meta, recatFormValues])

  if (!pane || !meta) {
    return null
  }

  return (
    subpaneChild
  )
}
